import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, GraduationCap, Plus, Search } from 'lucide-react';
import { useIsMobile } from 'hooks/use-media-query';
import { useSearchParamState } from 'hooks/use-search-param-state';
import { GroupsMobile } from 'components/mobile/screens/GroupsMobile';
import { Button } from 'components/ui/button';
import { PageHeader } from 'components/ui/page-header';
import { TableSearch } from 'components/ui/data-table';
import Skeleton from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import { formatGroupedNumber } from 'lib/dateFormat';
import * as TeamsModule from 'modules/teams';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { CreateTile } from 'pages/Library/CreateTile';
import { CreateTeamModal } from './CreateTeamModal';
import { GroupCard } from './GroupCard';

const GRID = 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3';

/** "jami 210 a'zo" with the number set in ink, whatever the language puts around it. */
function emphasiseCount(label: string, count: number): React.ReactNode {
  const raw = String(count);
  const at = label.indexOf(raw);
  if (at < 0) return label;
  return (
    <>
      {label.slice(0, at)}
      <span className="font-semibold tabular-nums text-foreground">{formatGroupedNumber(count)}</span>
      {label.slice(at + raw.length)}
    </>
  );
}

const TeamsListDesktop: React.FC = () => {
  const { t } = useTranslation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { input: search, setInput: setSearch } = useSearchParamState();

  const { teams, isLoading, isError, refetch } = TeamsModule.Hooks.useMyTeams();
  const { overview } = TeamsModule.Hooks.useOverview();

  // Permissions are the single source of truth — no role-string double-gating.
  const canManageTeams = usePermission(PERMISSIONS.TEAMS_CREATE);

  const needle = search.trim().toLocaleLowerCase();
  const filteredTeams = useMemo(
    () => (needle ? teams.filter((team) => team.name.toLocaleLowerCase().includes(needle)) : teams),
    [teams, needle],
  );

  const openCreate = () => setShowCreateModal(true);

  const renderBody = () => {
    if (isError) {
      return (
        <EmptyState
          icon={<AlertCircle size={28} />}
          title={t('failed_to_load')}
          secondaryAction={{ label: t('retry'), onClick: () => refetch() }}
        />
      );
    }
    if (isLoading) {
      return (
        <div className={GRID} aria-hidden>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[138px] rounded-lg" />
          ))}
        </div>
      );
    }
    if (teams.length === 0) {
      return (
        <EmptyState
          icon={<GraduationCap size={28} />}
          title={t('teams.empty_title')}
          description={t('teams.empty_desc')}
          action={
            canManageTeams
              ? { label: t('groups_create'), icon: <Plus className="h-4 w-4" />, onClick: openCreate }
              : undefined
          }
        />
      );
    }
    if (filteredTeams.length === 0) {
      return (
        <EmptyState
          icon={<Search size={28} />}
          title={t('teams.no_teams_match')}
          description={t('try_adjusting_your_search_or_filters')}
          secondaryAction={{ label: t('clear_search'), onClick: () => setSearch('') }}
        />
      );
    }
    return (
      <div className={GRID}>
        {filteredTeams.map((team) => (
          <GroupCard key={team.id} team={team} />
        ))}
        {canManageTeams && !needle && (
          <CreateTile label={t('groups_create')} onClick={openCreate} className="min-h-[138px]" />
        )}
      </div>
    );
  };

  return (
    <div id="teams-page-container" data-tour="teams-page" className="flex flex-col gap-4">
      <PageHeader
        title={
          <span className="flex items-center gap-2.5">
            {t('nav.groups')}
            {!isLoading && !isError && (
              <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                {teams.length}
              </span>
            )}
          </span>
        }
        actions={
          canManageTeams && (
            <Button size="sm" className="gap-1.5" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {t('groups_create')}
            </Button>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <TableSearch
          value={search}
          onChange={setSearch}
          placeholder={t('groups_search_placeholder')}
          aria-label={t('groups_search_placeholder')}
        />
        {typeof overview?.totalMembers === 'number' && (
          <span className="ml-auto whitespace-nowrap text-[13px] text-muted-foreground">
            {emphasiseCount(t('teams.members_total', { count: overview.totalMembers }), overview.totalMembers)}
          </span>
        )}
      </div>

      {renderBody()}

      <CreateTeamModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          refetch();
        }}
      />
    </div>
  );
};

// Phones render the dedicated mobile groups list; thin wrapper keeps hook count
// constant across the breakpoint.
export const TeamsListPage: React.FC = () => (useIsMobile() ? <GroupsMobile /> : <TeamsListDesktop />);

export default TeamsListPage;
